import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { useRangeKey } from '../TimeRangeControl';
import { fetchInfrastructure } from '../api';
import ChartTooltip from '../charts/ChartTooltip';
import Leaderboard, { type LeaderboardColumn } from '../charts/Leaderboard';
import Sparkline from '../charts/Sparkline';
import type { InfrastructurePayload } from '../types';
import { colorFor } from '../util';
import { BuiltAt, QUERY_OPTIONS, TabStatus } from './_shared';

type AsnRow = InfrastructurePayload['asn_leaderboard'][number];
type HalfLifeRow = InfrastructurePayload['ioc_half_life'][number];

const asnColumns: LeaderboardColumn<AsnRow>[] = [
  {
    key: 'rank',
    label: '#',
    width: '32px',
    align: 'right',
    render: (_row, i) => i + 1,
  },
  {
    key: 'asn',
    label: 'ASN',
    width: '90px',
    render: (row) => `AS${row.asn}`,
  },
  {
    key: 'org',
    label: 'Operator',
    render: (row) => row.asn_org || '—',
  },
  {
    key: 'country',
    label: 'Country',
    width: '80px',
    render: (row) => row.country || '—',
  },
  {
    key: 'quarterly',
    label: 'Quarterly',
    width: '160px',
    render: (row) => (
      <Sparkline
        data={row.quarterly.map((q) => ({ ym: q.q, n: q.n }))}
      />
    ),
  },
  {
    key: 'total',
    label: 'Mentions',
    align: 'right',
    width: '80px',
    render: (row) => row.total,
  },
];

const halfLifeColumns: LeaderboardColumn<HalfLifeRow>[] = [
  {
    key: 'type',
    label: 'IOC type',
    render: (row) => row.ioc_type,
  },
  {
    key: 'median',
    label: 'Median half-life',
    align: 'right',
    render: (row) => `${row.median_days} d`,
  },
  {
    key: 'samples',
    label: 'Samples',
    align: 'right',
    render: (row) => row.sample_size,
  },
];

function CountryChart({ counts }: { counts: Record<string, number> }) {
  const data = useMemo(
    () =>
      Object.entries(counts)
        .map(([country, n]) => ({ country, n }))
        .sort((a, b) => b.n - a.n)
        .slice(0, 20),
    [counts],
  );

  if (data.length === 0) {
    return <div className="trends-chart-empty">No country data.</div>;
  }
  return (
    <ResponsiveContainer width="100%" height={Math.max(200, data.length * 24)}>
      <BarChart data={data} layout="vertical" margin={{ top: 8, right: 24, bottom: 8, left: 24 }}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
        <XAxis type="number" fontSize={11} />
        <YAxis dataKey="country" type="category" fontSize={11} width={48} />
        <Tooltip content={<ChartTooltip />} />
        <Bar dataKey="n" name="IP mentions" isAnimationActive={false}>
          {data.map((_d, i) => (
            <Cell key={i} fill={colorFor(i)} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

export default function InfrastructureTab() {
  const range = useRangeKey();
  const q = useQuery({
    queryKey: ['trends', 'infrastructure', range],
    queryFn: () => fetchInfrastructure(range),
    ...QUERY_OPTIONS,
  });

  return (
    <section className="trends-tab-content trends-infrastructure">
      <TabStatus
        isLoading={q.isLoading}
        isError={q.isError}
        error={q.error}
        data={q.data}
        emptyHint="The trends_snapshot Dagster asset hasn’t produced Infrastructure data yet. It joins entities_v2 IP entities with asn_enrichment."
      >
        {q.data && (
          <>
            <BuiltAt at={q.data.built_at} />
            <div className="trends-infra-asns">
              <h2 className="trends-section-title">
                Top ASNs by IP mentions ({q.data.asn_leaderboard.length})
              </h2>
              <Leaderboard
                rows={q.data.asn_leaderboard}
                columns={asnColumns}
                rowKey={(row) => `asn-${row.asn}`}
              />
            </div>
            <div className="trends-infra-split">
              <div>
                <h2 className="trends-section-title">IOC half-life (first → last mention)</h2>
                <Leaderboard
                  rows={q.data.ioc_half_life}
                  columns={halfLifeColumns}
                  rowKey={(row) => row.ioc_type}
                />
              </div>
              <div>
                <h2 className="trends-section-title">Top hosting countries</h2>
                <CountryChart counts={q.data.country_counts} />
              </div>
            </div>
          </>
        )}
      </TabStatus>
    </section>
  );
}
